/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import { FormControl, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  VFGlobalContext,
  ADD_EDITING_DATA,
} from '@mummssoftware/global-context/VFGlobalContext';
import {
  abbreviateText,
  truncatePeriod,
  showFrequency,
  setFrequencyAtOne,
  setFrequencyAtZero,
} from '@mummssoftware/utils/frequencies/frequency-utils';
import { Q5006 } from '@mummssoftware/utils/constants';
import {
  Refused,
  ZeroFreq,
  Daily,
  BiWeekly,
  Weekly,
  Monthly,
  BiMonthly,
  AsNeeded,
  Discontinued,
  Assess,
  TwentyFourHours,
  OneXAssess,
} from '@mummssoftware/utils/frequencies/frequency-constants';
import {
  NurseRoleGroupId,
  AideRoleGroupId,
} from '@mummssoftware/utils/roleData';
import { CarePlanFrequencyContext } from '../../context';
import { CPFContext } from '../../containers/Context/CPFContext';
import { Frequency, periodAndFrequencyList } from '../../utils/constant';
import {
  getStagedOrderEntry,
  buildOrderEntryPayload,
  resolveCarePlanForRow,
} from './orderEntryHelpers';
import { isFreqNotEditable } from './utils';
import { resolveInlineFrequencySeed } from './cellEditState';
import {
  getWidgetEditMode,
  getCellEditRights,
  CPF_EDIT_MODE,
  CPF_SAVE_VIA,
  CpfEditMode,
} from '../../editPolicy';

const Selector = styled.div``;

export const EditFrequencyCell = (props: any) => {
  const { data, rgHash, isEditable, editMode: editModeProp, api, node } = props;
  const { roleGroupId, name, orderEntryId } = data;
  const { dispatchVF, VFordersMode, VForderData } = useContext(VFGlobalContext);
  const { editingRoleGroupId, isCPFAgency, isPRNOrderAgency } = useContext(
    CPFContext,
  );
  const { currentFacilityQcode, readOnly } = useContext(
    CarePlanFrequencyContext,
  );
  const useStyles = makeStyles((theme) => ({
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    menuPaper: {
      maxHeight: 200,
    },
  }));
  const classes = useStyles();

  const mode: CpfEditMode =
    editModeProp ||
    getWidgetEditMode({
      isCPFAgency: !!isCPFAgency,
      isPRNOrderAgency: !!isPRNOrderAgency,
      VFordersMode,
    });

  const isActiveRow =
    !!isEditable ||
    editModeProp === CPF_EDIT_MODE.ORDER ||
    (editingRoleGroupId != null &&
      String(editingRoleGroupId) === String(roleGroupId));

  const stagedEntry = getStagedOrderEntry(
    data,
    VForderData,
    VFordersMode,
    roleGroupId,
  );
  const carePlan =
    resolveCarePlanForRow(data, rgHash) ||
    rgHash[roleGroupId?.toString()]?.frequencies?.dummy;

  const rights = getCellEditRights({
    mode,
    column: Frequency,
    isActiveRow,
    carePlan,
    readOnly,
  });
  const { show: displayEdit, write: allowWrite } = rights;

  const dispatchOrderUpdate = (fields: Record<string, any>) => {
    if (rights.saveVia !== CPF_SAVE_VIA.ORDER) return;
    dispatchVF({
      type: ADD_EDITING_DATA,
      payload: buildOrderEntryPayload(
        { roleGroupId, name, orderEntryId },
        fields,
      ),
    });
  };

  const { period, frequency } = carePlan || {};

  const [statePeriod, setPeriod] = useState<string | null>(null);
  const [stateFrequency, setFrequency] = useState<number | undefined>(1);

  // Seed / restore period+frequency when writable.
  // Priority: Way 1 staged entry → in-progress edit* → saved plan.
  useEffect(() => {
    if (!allowWrite || !carePlan) return;

    const resolved = resolveInlineFrequencySeed({
      isOrderMode: mode === CPF_EDIT_MODE.ORDER,
      stagedEntry,
      carePlan,
    });
    if (resolved.period == null) {
      setPeriod(null);
      return;
    }
    carePlan.editPeriod = resolved.period;
    carePlan.editFrequency = resolved.frequency;
    setPeriod(resolved.period);
    setFrequency(resolved.frequency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowWrite, mode, carePlan?.id]);

  /** Periods that force frequency 1 or 0. */
  useEffect(() => {
    if (!allowWrite) return;
    if (
      statePeriod === BiWeekly ||
      statePeriod === BiMonthly ||
      statePeriod === Daily ||
      statePeriod === AsNeeded ||
      statePeriod === TwentyFourHours ||
      statePeriod === OneXAssess
    ) {
      setFrequency(1);
      if (carePlan) carePlan.editFrequency = 1;
      if (stagedEntry?.period) {
        dispatchOrderUpdate({
          frequency: 1,
          period: statePeriod,
        });
      }
    } else if (
      statePeriod === Refused ||
      statePeriod === ZeroFreq ||
      statePeriod === Discontinued
    ) {
      setFrequency(0);
      if (carePlan) carePlan.editFrequency = 0;
      if (stagedEntry?.period) {
        dispatchOrderUpdate({
          frequency: 0,
          period: statePeriod,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statePeriod, allowWrite]);

  const handlePeriodChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    if (!allowWrite) return;
    const selectedPeriod: any = event.target.value;
    let nextFrequency = stateFrequency;
    if (setFrequencyAtOne(selectedPeriod)) {
      nextFrequency = 1;
      setFrequency(1);
      if (carePlan) carePlan.editFrequency = 1;
    } else if (setFrequencyAtZero(selectedPeriod)) {
      nextFrequency = 0;
      setFrequency(0);
      if (carePlan) carePlan.editFrequency = 0;
    }
    const resolvedPeriod =
      selectedPeriod === OneXAssess ? Assess : selectedPeriod;
    if (carePlan) carePlan.editPeriod = resolvedPeriod;
    if (carePlan && isFreqNotEditable(resolvedPeriod)) {
      carePlan.editDaysOfWeek = '';
    }
    dispatchOrderUpdate({
      period: resolvedPeriod,
      frequency: nextFrequency,
      ...(isFreqNotEditable(resolvedPeriod) ? { daysOfWeek: undefined } : {}),
    });
    setPeriod(selectedPeriod as string);
    // Inline: period can unlock/lock Specific Days. Safe redraw — seed prefers edit*.
    if (rights.saveVia === CPF_SAVE_VIA.POST_CARE_PLAN && api && node) {
      setTimeout(() => {
        api.redrawRows({ rowNodes: [node] });
      }, 0);
    }
  };

  const handleFrequencyChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    if (!allowWrite) return;
    if (carePlan) carePlan.editFrequency = event.target.value;
    setFrequency(event.target.value as number);
    dispatchOrderUpdate({
      frequency: event.target.value,
      period: statePeriod,
    });
  };

  const listPeriodMenuItems = () => {
    const periodList = periodAndFrequencyList.map(
      (listedPeriod) => listedPeriod.period,
    );

    const showIPUPeriod =
      (NurseRoleGroupId === roleGroupId || AideRoleGroupId === roleGroupId) &&
      Q5006 === currentFacilityQcode;

    return periodList.map((p) => {
      const showFreq =
        p &&
        (p !== TwentyFourHours || (p === TwentyFourHours && showIPUPeriod));

      if (showFreq)
        return (
          <MenuItem
            key={p}
            value={p}
            style={{
              fontSize: '.9rem',
            }}
          >
            {p}
          </MenuItem>
        );
      return null;
    });
  };

  const listFrequencyMenuItems = () => {
    let frequencyList: number[] | undefined = [];

    frequencyList = periodAndFrequencyList.find(
      (listItem) => listItem.period === statePeriod,
    )?.frequencies;
    if (!frequencyList) frequencyList = [0];

    return frequencyList.map((f) => (
      <MenuItem
        key={`frequency-${f}`}
        value={f}
        style={{
          fontSize: '.9rem',
        }}
      >
        {f}
      </MenuItem>
    ));
  };

  const periodId = stagedEntry?.period || data.stagedPeriod;
  const stagedFrequencyValue = stagedEntry?.frequency ?? data.stagedFrequency;
  const hasStagedFrequency =
    data.isOrderPending || stagedEntry?.period || data.stagedPeriod;

  const getFreqText = () => {
    let freqText = '';
    if (periodId) {
      if (periodId === Weekly || periodId === Monthly) {
        freqText = `${periodId} - ${stagedFrequencyValue ?? ''}`;
      } else {
        freqText = periodId;
      }
    } else if (!hasStagedFrequency && carePlan?.frequencyText) {
      freqText = truncatePeriod(
        abbreviateText(carePlan.frequencyText, carePlan.period),
      );
    }
    return freqText;
  };

  return displayEdit ? (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <div
        style={{
          width: '80px',
        }}
      >
        <Selector className="cpfSelector">
          <FormControl>
            <Select
              style={{
                fontSize: '.9rem',
              }}
              id="roleGroupInfoSelectorPeriod"
              value={statePeriod}
              onChange={handlePeriodChange}
              defaultValue={period || 1}
              MenuProps={{
                classes: {
                  paper: classes.menuPaper,
                },
              }}
            >
              {listPeriodMenuItems()}
            </Select>
          </FormControl>
        </Selector>
      </div>
      {showFrequency(statePeriod) && (
        <div>
          <Selector className="cpfSelector">
            <FormControl>
              <Select
                style={{
                  fontSize: '.9rem',
                }}
                id="roleGroupInfoSelectorFrequency"
                value={stateFrequency}
                onChange={handleFrequencyChange}
                defaultValue={frequency || Daily}
                MenuProps={{
                  classes: {
                    paper: classes.menuPaper,
                  },
                }}
              >
                {listFrequencyMenuItems()}
              </Select>
            </FormControl>
          </Selector>
        </div>
      )}
    </div>
  ) : (
    <div>{getFreqText()}</div>
  );
};

export default EditFrequencyCell;
